import React, { FunctionComponent, useState } from 'react';
import { any, map, filter, pipe, uniqBy, prop } from 'ramda';
import { ICandidate } from '../../common/models/candidate/ICandidate';
import { ICandidatePositionProfile } from '../../common/models/candidate/ICandidatePositionProfile';
import ArchiveCandidateModal from '../ArchiveCandidateModal';
import SendReminderModal from '../SendReminderModal';
import { CandidateStatus } from '../../common/enums/CandidateStatus';
import ChangeOwner from '../ChangeOwner';
import RequestAdditionReferences from '../RequestAdditionReferences';
import Icon from '../../components/Icon';
import RegularButton from '../../components/RegularButton';
import { updateCandidateOwnerRoutine } from './routines';
import { connect } from 'react-redux';
import { IBindingCallback1 } from '../../common/models/callback';
import { IUpdateOwner } from '../../common/models/candidate/IUpdateOwner';
import { IMergedContactInfo } from '../../common/models/contact/IMergedContactInfo';
import ModalWindow from '../../components/ModalWindow';
import MergeContacts from '../MergeContacts';
import IconButton from '../../components/IconButton';
import { setReportCandidatesRoutine } from '../ShareReportModal/routines';

interface ICandidatesBulkActionsProps {
  candidates: (ICandidate | ICandidatePositionProfile)[];
  updateCandidateOwner: IBindingCallback1<IUpdateOwner>;
  setReportCandidates: IBindingCallback1<string[]>;
}

type CandidateBulkAction = 'requestMore' | 'reminder' | 'changeOwner' | 'merge' | 'report' | 'archive';

const CandidatesBulkActions: FunctionComponent<ICandidatesBulkActionsProps> = ({
  candidates,
  setReportCandidates,
  updateCandidateOwner
}) => {
  const [activeAction, setActiveAction] = useState<CandidateBulkAction | null>(null);

  const reportCandidates = pipe<
    Array<(ICandidate | ICandidatePositionProfile)>,
    Array<(ICandidate | ICandidatePositionProfile)>,
    string[]
      >(
      filter(({ referenceProgress }: ICandidate | ICandidatePositionProfile) => Boolean(referenceProgress)),
      map(({ id }) => id))(candidates);

  const ids = map(({ id }) => id, candidates);

  const onHide = () => setActiveAction(null);

  const contacts = pipe<(ICandidate | ICandidatePositionProfile)[],
    (ICandidate | ICandidatePositionProfile)[],
    IMergedContactInfo[]
    >(
    uniqBy(prop('contactId')),
    map(contact => ({
      id: contact.contactId,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      lastModifiedDate: contact.lastModifiedDate
    })))(candidates);

  const isReminderDisabled = any(c => c.status !== CandidateStatus.Active, candidates);
  const isReportDisabled = !reportCandidates.length;

  return (
    <div className="bulk-actions">
      <RequestAdditionReferences candidates={candidates} />
      <SendReminderModal isShown={activeAction === 'reminder'} onHide={onHide} candidates={candidates} />
      <RegularButton
        textIntlId="iconButtonLable.reminder"
        defaultMessage="Reminder"
        variant="tertiary"
        icon={<Icon icon="reminder" />}
        disabled={isReminderDisabled}
        onClick={() => setActiveAction('reminder')}
        tooltip={isReminderDisabled
          ? { id: 'bulkDisabled.inactive', defaultMessage: 'Candidates should be active' }
          : undefined}
      />
      <ChangeOwner
        isShown={activeAction === 'changeOwner'}
        onHide={onHide}
        ids={ids}
        variant="candidates"
        updateOwner={updateCandidateOwner}
      />
      <RegularButton
        textIntlId="iconButtonLable.reassign"
        defaultMessage="Reassign"
        variant="tertiary"
        icon={<Icon icon="userChange" />}
        onClick={() => setActiveAction('changeOwner')}
      />
      <RegularButton
        textIntlId="iconButtonLable.merge"
        defaultMessage="Merge"
        icon={<Icon icon="merge" />}
        disabled={contacts.length < 2 || contacts.length > 5}
        onClick={() => setActiveAction('merge')}
        variant="tertiary"
      />
      <ModalWindow
        isShown={activeAction === 'merge'}
        onHide={onHide}
        contentClassName="big-modal modal-wrapper"
      >
        <MergeContacts onHide={onHide} contacts={contacts} />
      </ModalWindow>
      <IconButton
        disabled={isReportDisabled}
        icon={<Icon icon="chart" />}
        tooltip={isReportDisabled
          ? { id: 'bulkDisabled.report', defaultMessage: 'No reference responses yet' }
          : { id: 'runReport', defaultMessage: 'Run report' }}
        variant="bordered"
        onClick={() => setReportCandidates(ids)}
      />
      <ArchiveCandidateModal onHide={onHide} isShown={activeAction === 'archive'} candidates={ids} />
      <IconButton
        variant="bordered"
        icon={<Icon icon="archive" />}
        onClick={() => setActiveAction('archive')}
        tooltip={{ id: 'iconButtonLable.archive', defaultMessage: 'Archive' }}
      />
    </div>
  );
};

const mapDispachToProps = {
  updateCandidateOwner: updateCandidateOwnerRoutine,
  setReportCandidates: setReportCandidatesRoutine
};

export default connect(null, mapDispachToProps)(CandidatesBulkActions);
